<template>
  <div class="academic-year-container">
    <div class="page-header">
      <div class="breadcrumb">ວິຊາການ / <span>ຈັດການສົກຮຽນ</span></div>
      <div class="header-content">
        <h1>ລາຍຊື່ສົກຮຽນທັງໝົດ</h1>
        <button v-if="hasBit('academic-new-year', 1)" class="btn-primary" @click="$router.push('/home/academic/year/new')">
          <i class="fas fa-plus"></i> ເພີ່ມສົກຮຽນໃໝ່
        </button>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <div class="search-box">
          <div class="input-container">
            <i class="fas fa-search search-icon"></i>
            <input type="text" v-model="searchQuery" @keyup.enter="handleSearch" placeholder="ຄົ້ນຫາສົກຮຽນ...">
          </div>
          <button class="btn-search" @click="handleSearch">ຄົ້ນຫາ</button>
        </div>
      </div>

      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th width="80">ລຳດັບ</th>
                <th width="150">ລະຫັດສົກຮຽນ</th>
                <th>ຊື່ສົກຮຽນ</th>
                <th class="text-center" width="150">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="4" class="text-center py-4"><i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດ...</td></tr>
              <tr v-else v-for="(item, index) in years" :key="item._id">
                <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                <td><span class="asset-code">{{ item.id }}</span></td>
                <td class="asset-name">{{ item.accd }}</td>
                <td class="text-center">
                  <button v-if="hasBit('academic-new-year', 1)" class="panel-action" @click="handleEdit(item)" title="ແກ້ໄຂ">✏️</button>
                  <button v-if="hasBit('academic-new-year', 2)" class="panel-action text-danger" @click="confirmDelete(item)" title="ລົບ">🗑️</button>
                </td>
              </tr>
              <tr v-if="!loading && years.length === 0"><td colspan="4" class="text-center py-4">ບໍ່ມີຂໍ້ມູນສົກຮຽນ</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination-container">
        <div class="pagination-info">ສະແດງ {{ startItem }} - {{ endItem }} ຈາກ {{ totalRows }} ລາຍການ</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">«</button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'e' + idx" class="page-ellipsis">…</span>
            <button v-else :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="changePage(page)">{{ page }}</button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">»</button>
        </div>
      </div>
    </div>

    <!-- Result Modal (Reuse common style) -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="resultModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type"><i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i></div>
          <h2>{{ resultModal.title }}</h2>
          <p>{{ resultModal.message }}</p>
          <button class="btn-modal-primary" @click="resultModal.show = false">ເຂົ້າໃຈແລ້ວ</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import { getPaginationRange } from '@/utils/pagination';

export default {
  name: 'AcademicYearListPage',
  data() {
    return {
      years: [],
      currentPage: 1,
      pageSize: 20,
      totalRows: 0,
      searchQuery: '',
      loading: false,
      resultModal: { show: false, type: 'success', title: '', message: '' },
      permissionState: {},
      permissionRecordId: null,
      loadingPermissions: false,    
      user: {}
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.totalRows / this.pageSize) || 1; },
    visiblePages() { return getPaginationRange(this.currentPage, this.totalPages); },
    startItem() { return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    endItem() { const end = this.currentPage * this.pageSize; return end > this.totalRows ? this.totalRows : end; }
  },
  mounted() { 
    this.fetchYears(); 
    this.user = JSON.parse(localStorage.getItem("user-info") || "{}");
    
    this.fetchRolePermissions(this.user.user_level);
  },
  methods: {
    async fetchYears() {
      this.loading = true;
      try {
        const res = await api.get('/accd/all', { params: { page: this.currentPage, limit: this.pageSize, search: this.searchQuery } });
        if (res.data.code === 1) { this.years = res.data.rows; this.totalRows = res.data.totalRows; }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    handleSearch() { this.currentPage = 1; this.fetchYears(); },
    changePage(page) { if (page >= 1 && page <= this.totalPages) { this.currentPage = page; this.fetchYears(); } },
    handleEdit(item) { this.$router.push(`/home/academic/year/edit/${item._id}`); },
    async confirmDelete(item) {
      if (confirm(`ຕ້ອງການລົບສົກຮຽນ: ${item.accd} ແມ່ນບໍ່?`)) {
        try {
          const res = await api.delete(`/del/accd/${item._id}`);
          if (res.data.code === 1) { this.showResult('success', 'ສຳເລັດ', 'ລົບຂໍ້ມູນສົກຮຽນຮຽບຮ້ອຍແລ້ວ'); this.fetchYears(); }
        } catch (e) { this.showResult('error', 'ຜິດພາດ', 'ບໍ່ສາມາດລົບຂໍ້ມູນໄດ້'); }
      }
    },
    showResult(type, title, message) { this.resultModal = { show: true, type, title, message }; }
    ,
     async fetchRolePermissions(roleId) {
      this.loadingPermissions = true;
      try {
        const res = await api.get(`/getRowsInt/permissions/role_id/${roleId}`);
        console.log('Permissions response:', res.data);
        if (res.data && res.data.length > 0) {
          this.permissionState = res.data[0].permissions || {};
          this.permissionRecordId = res.data[0]._id;
        } else {
          // ຫາກບໍ່ພົບຂໍ້ມູນ ໃຫ້ຕັ້ງຄ່າເລີ່ມຕົ້ນ
          this.permissionRecordId = null;
          const defaults = {};
          this.menuGroups.forEach(group => {
            // ຕັ້ງຄ່າເລີ່ມຕົ້ນໃຫ້ກຸ່ມຫຼັກ
            defaults[group.id] = roleId === 1 ? 7 : 1;
            group.items.forEach(item => {
              // ຖ້າເປັນ Admin ໃຫ້ສິດເຕັມ (7), ຖ້າບໍ່ແມ່ນໃຫ້ສິດເບິ່ງຢ່າງດຽວ (1)
              defaults[item.id] = roleId === 1 ? 7 : 1;
            });
          });
          this.permissionState = defaults;
        }
      } catch (e) {
        console.error('Fetch permissions error:', e);
      } finally {
        this.loadingPermissions = false;
      }
    },
    hasBit(moduleKey, bit) {
      const val = this.permissionState[moduleKey] || 0;
      return (val & (1 << bit)) !== 0;
    },
  }
};
</script>

<style scoped>
.academic-year-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.search-box { display: flex; gap: 10px; padding: 15px 20px; background: #fcfcfc; border-radius: 10px 10px 0 0; }
.input-container { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); }
.input-container input { width: 100%; padding: 10px 10px 10px 35px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; }
.btn-search { background: var(--lao); color: white; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; font-weight: 500; }
.asset-code { background: #e2e8f0; padding: 2px 8px; border-radius: 4px; font-family: monospace; font-size: 11px; font-weight: 600; color: #475569; }
.asset-name { color: var(--lao); font-weight: 600; }
.pagination-container { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-top: 1px solid var(--border); }
.page-btn { padding: 5px 12px; border: 1px solid var(--border); background: white; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.page-btn.active { background: var(--lao); color: white; border-color: var(--lao); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-ellipsis { padding: 5px 6px; color: var(--text3); }
.btn-primary { background: var(--lao); color: white; border: none; padding: 10px 20px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-weight: 500; }
.text-center { text-align: center; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>